import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import CommonTextArea from "@/shared/components/ui/Input/CommonTextArea";
import ConfirmModal from "@/shared/components/ui/Modal/ConfirmModal";
import Loader from "@/shared/components/ui/Loaders/Loader";
import { showErrorFromMutation, showSuccess } from "@/shared/utils/toast";
import { getTextInCapitalize } from "@/shared/utils/helperFunctions";
import {
  approveVoiceOrganization,
  fetchVoiceApprovals,
  rejectVoiceOrganization,
} from "./api/superadminApi";
import { VOICE_ORGS_QUERY_KEY } from "./VoiceOrgModal";

const APPROVALS_QUERY_KEY = ["super-admin-voice-approvals"];

const STATUS_BADGES = {
  pending: ["Pending", "bg-yellow-100 text-yellow-700"],
  approved: ["Approved", "bg-green-100 text-green-700"],
  rejected: ["Declined", "bg-red-100 text-red-700"],
};

const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });
const formatDate = (value) => (value ? dateTime.format(new Date(value)) : "-");

const CallingApprovals = () => {
  const queryClient = useQueryClient();
  const [declining, setDeclining] = useState(null);
  const [declineNote, setDeclineNote] = useState("");
  const { data: requests = [], isLoading } = useQuery({
    queryKey: APPROVALS_QUERY_KEY,
    queryFn: fetchVoiceApprovals,
  });

  const onDecided = (message) => {
    queryClient.invalidateQueries({ queryKey: APPROVALS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: VOICE_ORGS_QUERY_KEY });
    showSuccess(message);
  };

  const approve = useMutation({
    mutationFn: (orgId) => approveVoiceOrganization(orgId),
    onSuccess: () => onDecided("Voice calling approved."),
    onError: (error) => showErrorFromMutation(error, "Could not approve this request."),
  });
  const reject = useMutation({
    mutationFn: ({ orgId, note }) => rejectVoiceOrganization(orgId, note),
    onSuccess: () => onDecided("Request declined."),
    onError: (error) => showErrorFromMutation(error, "Could not decline this request."),
  });
  const isBusy = approve.isPending || reject.isPending;

  const closeDecline = () => {
    setDeclining(null);
    setDeclineNote("");
  };

  const pendingCount = requests.filter((row) => row.approval_status === "pending").length;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-[1.375rem] text-gray-800 font-bold">Calling Approvals</h1>
        <p className="mt-1 text-sm text-gray-500">
          Organizations can create a calling account and buy numbers only after you approve them.
          {pendingCount > 0 ? ` ${pendingCount} waiting.` : ""}
        </p>
      </div>
      <div className="rounded-xl bg-white p-4 shadow w-full">
        {isLoading ? (
          <Loader text="Loading requests..." />
        ) : requests.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-500">No calling requests yet.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-gray-100">
            {requests.map((row) => {
              const [label, tone] = STATUS_BADGES[row.approval_status] || STATUS_BADGES.pending;
              return (
                <li
                  key={row.organization_id}
                  className="flex flex-col gap-3 py-3 md:flex-row md:items-center md:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-gray-800">
                        {getTextInCapitalize(row.organization_name || "")}
                      </p>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>
                        {label}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Requested by {row.requested_by_name || row.requested_by_email || "unknown"}
                      {row.requested_by_email && row.requested_by_name ? ` (${row.requested_by_email})` : ""}
                      {" · "}
                      {formatDate(row.approval_requested_at)}
                    </p>
                    {row.approval_status !== "pending" && (
                      <p className="mt-0.5 text-xs text-gray-500">
                        {label} {formatDate(row.approval_decided_at)}
                        {row.decided_by_name ? ` by ${row.decided_by_name}` : ""}
                        {row.approval_note ? ` · ${row.approval_note}` : ""}
                      </p>
                    )}
                  </div>
                  {row.approval_status === "pending" && (
                    <div className="flex shrink-0 gap-2">
                      <SecondaryButton
                        title="Decline"
                        onClick={() => setDeclining(row)}
                        disabled={isBusy}
                      />
                      <PrimaryButton
                        startContent={false}
                        onClick={() => approve.mutate(row.organization_id)}
                        isLoading={approve.isPending && approve.variables === row.organization_id}
                        disabled={isBusy}
                      >
                        Approve
                      </PrimaryButton>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ConfirmModal
        isOpen={Boolean(declining)}
        destructive
        onClose={closeDecline}
        title={`Decline ${getTextInCapitalize(declining?.organization_name || "this request")}?`}
        description="The organization admin sees your reason and can request again later."
        confirmText="Decline"
        isLoading={reject.isPending}
        onConfirm={() =>
          reject.mutate(
            { orgId: declining.organization_id, note: declineNote.trim() || undefined },
            { onSettled: closeDecline },
          )
        }
      >
        <CommonTextArea
          value={declineNote}
          onChange={(event) => setDeclineNote(event.target.value)}
          placeholder="Reason (optional)"
          rows={3}
          maxLength={500}
          disabled={reject.isPending}
        />
      </ConfirmModal>
    </div>
  );
};

export default CallingApprovals;
